import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// ============================================
// HERO SECTION
// ============================================
const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-16 md:pt-20 pb-20 md:pb-0 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 hidden md:block w-[600px] h-[600px] bg-neon-orange/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 hidden md:block w-[500px] h-[500px] bg-neon-orange/5 rounded-full blur-[100px]" />
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(253,128,46,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(253,128,46,0.5) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark-base/50 to-dark-base" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-neon-orange/10 border border-neon-orange/30 mb-4 sm:mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.2 }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-orange opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-neon-orange"></span>
              </span>
              <span className="text-neon-orange text-xs sm:text-sm font-medium">Enterprise AI Audio Platform</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-[1.1]">
              VeritoneTech AI
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange via-orange-400 to-yellow-500">
                Enterprise Audio Intelligence
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-6 md:mb-8 max-w-lg leading-relaxed">
              VeritoneTech AI delivers enterprise-grade AI audio processing with 
              real-time analytics, visual workflow orchestration, and rapid model deployment.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 mb-8 md:mb-10">
              <motion.button
                className="group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-neon-orange to-orange-500 text-white font-bold rounded-xl text-base sm:text-lg inline-flex items-center justify-center gap-2 sm:gap-3 shadow-[0_0_40px_rgba(253,128,46,0.4)] w-full sm:w-auto"
                onClick={() => { window.open('https://app.veritonetech.com/', '_blank'); }}
                aria-label="Start Free Trial - Open VeritoneTech app"
                whileHover={{ scale: 1.02, boxShadow: '0 0 60px rgba(253,128,46,0.6)' }}
                whileTap={{ scale: 0.98 }}
              >
                Start Free Trial
                <motion.svg 
                  className="w-5 h-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </motion.svg>
              </motion.button>
              
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col xs:flex-row flex-wrap items-start gap-2 xs:gap-3 sm:gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                <svg className="w-4 sm:w-5 h-4 sm:h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                SOC 2 Certified
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                14-day free trial
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                No credit card required
              </div>
            </div>
          </motion.div>

          {/* Right: Dashboard Preview - Hidden on mobile */}
          <motion.div
            className="hidden lg:block relative"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Main Dashboard Card */}
              <div className="bg-dark-deeper/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-dark-base/80 px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <span className="text-gray-400 text-sm ml-2">VeritoneTech Dashboard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-green-500 text-xs">Live</span>
                  </div>
                </div>

                {/* Dashboard Content */}
                <div className="p-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    {[
                      { label: 'Files Processed', value: '12,847', trend: '+12%' },
                      { label: 'Active Workflows', value: '24', trend: '+3' },
                      { label: 'API Calls', value: '1.2M', trend: '+8%' },
                    ].map((stat, i) => (
                      <motion.div
                        key={stat.label}
                        className="bg-white/5 rounded-xl p-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.5 + i * 0.1 }}
                      >
                        <p className="text-gray-500 text-xs mb-1">{stat.label}</p>
                        <div className="flex items-end justify-between">
                          <span className="text-white font-bold text-xl">{stat.value}</span>
                          <span className="text-green-500 text-xs">{stat.trend}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Waveform Visualization */}
                  <div className="bg-dark-base/50 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-sm">Audio Processing</span>
                      <span className="text-neon-orange text-xs">Real-time</span>
                    </div>
                    <div className="overflow-x-auto">
                      <div className="inline-flex items-end gap-[3px] h-16 min-w-max">
                        {[...Array(50)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1 bg-gradient-to-t from-neon-orange/50 to-neon-orange rounded-full"
                            animate={{
                              height: ['15%', `${20 + Math.random() * 80}%`, '15%'],
                            }}
                            transition={{
                              duration: 0.8 + Math.random() * 0.5,
                              repeat: Infinity,
                              delay: i * 0.02,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['Models', 'Workflow', 'Library', 'Analytics'].map((action, i) => (
                      <motion.div
                        key={action}
                        className="bg-white/5 hover:bg-neon-orange/10 rounded-lg p-3 text-center cursor-pointer transition-colors border border-transparent hover:border-neon-orange/30"
                        whileHover={{ y: -2 }}
                      >
                        <span className="text-gray-400 text-xs">{action}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute top-4 right-4 md:-top-4 md:-right-4 px-4 py-2 bg-gradient-to-r from-neon-orange to-orange-500 rounded-full text-white font-bold text-sm shadow-lg"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                NEW v2.0
              </motion.div>

              <motion.div
                className="absolute bottom-4 left-4 md:-bottom-6 md:-left-6 bg-dark-deeper/90 backdrop-blur-xl rounded-xl p-4 border border-white/10 shadow-xl max-w-xs md:max-w-none"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.8 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Processing Complete</p>
                    <p className="text-gray-500 text-xs">1,247 files in 3.2s</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Logos Section */}
        <motion.div
          className="mt-8 md:mt-20 pt-6 md:pt-12 border-t border-white/10"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1 }}
        >
          <p className="text-center text-gray-500 text-xs sm:text-sm mb-6 md:mb-8">Trusted by leading companies worldwide</p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 opacity-50">
            {['Spotify', 'Adobe', 'Netflix', 'Amazon', 'Google', 'Microsoft'].map((company) => (
              <span key={company} className="text-gray-400 font-heading text-sm md:text-xl">{company}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// FEATURES SECTION
// ============================================
const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "AI Audio Models",
      description: "Text-to-speech, voice cloning, music generation, transcription, and audio enhancement powered by cutting-edge AI models.",
      highlights: ["Voice Cloning", "Text-to-Speech", "Transcription", "Music Generation"],
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      title: "Visual Workflow Builder",
      description: "Drag-and-drop audio processing pipelines with noise reduction, EQ, compression, and AI enhancement steps.",
      highlights: ["Drag & Drop", "Custom Pipelines", "Auto-Processing", "Templates"],
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      title: "Audio Library Management",
      description: "Organize, search, filter, and play audio files with comprehensive status tracking and metadata management.",
      highlights: ["Smart Search", "Batch Operations", "Metadata Editor", "Version Control"],
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Real-time Analytics",
      description: "Live dashboards with performance metrics, usage trends, workflow analytics, and customizable reports.",
      highlights: ["Live Metrics", "Usage Reports", "Performance Tracking", "Export Data"],
      color: "from-neon-orange to-yellow-500",
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "User & Team Management",
      description: "Role-based access control, user activity tracking, team collaboration tools, and audit logging.",
      highlights: ["Role-Based Access", "Activity Logs", "Team Workspaces", "SSO Support"],
      color: "from-red-500 to-rose-500",
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Customizable Settings",
      description: "Appearance themes, third-party integrations, notification preferences, and billing management.",
      highlights: ["Dark/Light Themes", "API Webhooks", "Custom Branding", "Billing Portal"],
      color: "from-indigo-500 to-purple-500",
    },
  ];

  return (
    <section id="features" ref={ref} className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block w-[1000px] h-[1000px] bg-neon-orange/5 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            className="inline-block px-4 py-2 rounded-full bg-neon-orange/10 text-neon-orange text-sm font-medium mb-4 border border-neon-orange/20"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            Powerful Features
          </motion.span>
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            Everything You Need for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-yellow-500">
              Enterprise Audio Processing
            </span>
          </motion.h2>
          <motion.p
            className="text-gray-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            A complete suite of tools designed for teams that demand the best in AI audio technology.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className={`group relative bg-dark-deeper/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5 hover:border-neon-orange/30 transition-all duration-500 cursor-pointer ${
                activeFeature === i ? 'border-neon-orange/50 bg-neon-orange/5' : ''
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => setActiveFeature(i)}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

              {/* Icon */}
              <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} bg-opacity-10 flex items-center justify-center text-white mb-5`}>
                {feature.icon}
                <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${feature.color} opacity-20`} />
              </div>

              {/* Content */}
              <h3 className="text-xl font-heading font-bold text-white mb-3 group-hover:text-neon-orange transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                {feature.description}
              </p>

              {/* Highlights */}
              <div className="flex flex-wrap gap-2">
                {feature.highlights.map((highlight) => (
                  <span
                    key={highlight}
                    className="px-2 py-1 bg-white/5 rounded-md text-gray-500 text-xs"
                  >
                    {highlight}
                  </span>
                ))}
              </div>

              {/* Arrow */}
              <motion.div
                className="absolute bottom-6 right-6 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// BENEFITS SECTION
// ============================================
const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const benefits = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      stat: "10,000+",
      title: "Files Per Batch",
      description: "Process thousands of audio files simultaneously with our distributed processing engine.",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      stat: "99.9%",
      title: "Uptime SLA",
      description: "Enterprise-grade reliability with guaranteed uptime and 24/7 monitoring.",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      stat: "SOC 2",
      title: "Security & Compliance",
      description: "Enterprise security with SOC 2 Type II, GDPR, and HIPAA compliance.",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      stat: "RESTful API",
      title: "Developer API",
      description: "Full API access with comprehensive documentation, SDKs, and webhooks.",
    },
  ];

  return (
    <section id="benefits" ref={ref} className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-neon-orange/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-neon-orange/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-neon-orange/10 text-neon-orange text-sm font-medium mb-4 border border-neon-orange/20">
              Why VeritoneTech
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Built for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-yellow-500"> Enterprise</span>
              <br />Scale & Security
            </h2>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg mb-8 leading-relaxed">
              From startups to Fortune 500 companies, VeritoneTech Dashboard handles 
              millions of audio files with enterprise-grade security and reliability.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={benefit.title}
                  className="group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-neon-orange/10 flex items-center justify-center text-neon-orange flex-shrink-0 group-hover:bg-neon-orange/20 transition-colors">
                      {benefit.icon}
                    </div>
                    <div>
                      <span className="text-xl sm:text-2xl font-heading font-bold text-neon-orange">{benefit.stat}</span>
                      <h4 className="text-white font-semibold mt-1">{benefit.title}</h4>
                      <p className="text-gray-500 text-sm mt-1">{benefit.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Visual - Hidden on mobile */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="hidden lg:block relative w-full"
          >
            <div className="relative bg-dark-deeper/50 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/10 w-full max-w-full overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-neon-orange/20 via-transparent to-neon-orange/20 rounded-3xl blur-xl opacity-50" />
              
              <div className="relative">
                {/* Code/Terminal Preview */}
                <div className="bg-dark-base rounded-xl p-4 font-mono text-sm mb-6 overflow-hidden">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-gray-500 text-xs ml-2">api-request.js</span>
                  </div>
                  <pre className="text-gray-400 overflow-x-auto break-words">
                    <code>
{`const response = await veritone.audio.process({
  files: audioFiles,
  pipeline: [
    { type: 'noise_reduction', level: 0.8 },
    { type: 'enhance', model: 'clarity_v2' },
    { type: 'transcribe', language: 'en' }
  ],
  webhook: 'https://your-app.com/callback'
});

// ✓ Processing 10,247 files...`}
                    </code>
                  </pre>
                </div>

                {/* Progress indicators */}
                <div className="space-y-4">
                  {[
                    { label: 'Audio Enhancement', progress: 94 },
                    { label: 'Voice Cloning Accuracy', progress: 98 },
                    { label: 'Transcription Quality', progress: 99 },
                  ].map((item, i) => (
                    <div key={item.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">{item.label}</span>
                        <span className="text-neon-orange">{item.progress}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-neon-orange to-orange-400 rounded-full"
                          initial={{ width: 0 }}
                          animate={isInView ? { width: `${item.progress}%` } : {}}
                          transition={{ delay: 0.5 + i * 0.2, duration: 1 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TECHNOLOGY SECTION
// ============================================
const TechnologySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="tech" ref={ref} className="py-24 relative">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block w-[800px] h-[800px] bg-neon-orange/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <motion.span
            className="inline-block px-4 py-2 rounded-full bg-neon-orange/10 text-neon-orange text-sm font-medium mb-4 border border-neon-orange/20"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            Powered by NVIDIA AI
          </motion.span>
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            VeritoneTech AI Technology Stack
          </motion.h2>
          <motion.p
            className="text-gray-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            A unified platform built on NVIDIA NeMo, DeepStream, Riva, RAPIDS, Triton, and TensorRT for high-performance generative audio, real-time pipeline orchestration, and scale.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
          {[
            {
              title: 'AI Model Factory',
              sdk: 'NVIDIA NeMo Framework',
              details: 'Train and customize text-to-speech, ASR, voice cloning, music generation, and enhancement models in an enterprise-grade AI pipeline.',
            },
            {
              title: 'Workflow Orchestration',
              sdk: 'NVIDIA DeepStream SDK',
              details: 'GPU-accelerated graph-based audio workflow builder with low latency and high throughput on audio stream processing.',
            },
            {
              title: 'Real-time STT/TTS',
              sdk: 'NVIDIA Riva',
              details: 'Production-ready low-latency speech recognition and TTS inference with customizable domain models.',
            },
            {
              title: 'Analytics Engine',
              sdk: 'NVIDIA RAPIDS',
              details: 'High-speed GPU analytics for real-time usage trends, performance monitoring, and dashboard insights.',
            },
            {
              title: 'Unified Deployment',
              sdk: 'NVIDIA Triton + TensorRT',
              details: 'Optimize and serve all AI services and pipelines with a scalable, enterprise production inference layer.',
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              className="bg-dark-deeper/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.1 }}
            >
              <h3 className="text-xl font-heading font-bold text-white mb-2">{item.title}</h3>
              <p className="text-neon-orange font-semibold mb-2">{item.sdk}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.details}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FAQ SECTION
// ============================================
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(-1);

  const faqs = [
    {
      question: "What AI models are included?",
      answer: "VeritoneTech Dashboard includes text-to-speech, voice cloning, speech-to-text transcription, music generation, audio enhancement, noise reduction, and more. Enterprise plans can also include custom model training.",
    },
    {
      question: "How does the visual workflow builder work?",
      answer: "Our drag-and-drop workflow builder lets you create audio processing pipelines visually. Connect modules like noise reduction, EQ, compression, AI enhancement, and export steps. Save workflows as templates for batch processing.",
    },
    {
      question: "Is there an API for developers?",
      answer: "Yes! Professional and Enterprise plans include full REST API access with comprehensive documentation, SDKs for popular languages (Python, Node.js, Go), webhooks, and rate limits up to 10,000 requests per minute.",
    },
    {
      question: "What security certifications do you have?",
      answer: "We are SOC 2 Type II certified, GDPR compliant, and offer HIPAA-compliant processing for healthcare customers. All data is encrypted at rest and in transit with AES-256 encryption.",
    },
    {
      question: "Can I try before I buy?",
      answer: "Absolutely! All plans include a 14-day free trial with full access to features. No credit card required to start. Enterprise customers can also request a personalized demo.",
    },
    {
      question: "What support options are available?",
      answer: "Starter plans include email support. Professional plans add priority support with 4-hour response time. Enterprise customers get 24/7 dedicated support, a customer success manager, and custom SLAs.",
    },
  ];

  return (
    <section id="faq" ref={ref} className="py-24 relative">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <motion.span
            className="inline-block px-4 py-2 rounded-full bg-neon-orange/10 text-neon-orange text-sm font-medium mb-4 border border-neon-orange/20"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            FAQ
          </motion.span>
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            Frequently Asked Questions
          </motion.h2>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              className="bg-dark-deeper/50 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.05 }}
            >
              <button
                className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
              >
                <span className="text-white font-semibold text-sm sm:text-base pr-4">{faq.question}</span>
                <motion.svg
                  className="w-5 h-5 text-neon-orange flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  animate={{ rotate: openIndex === i ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </motion.svg>
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-4 sm:px-6 pb-4 sm:pb-5 text-gray-400 text-sm sm:text-base">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CTA SECTION
// ============================================
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 relative">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          className="relative bg-gradient-to-br from-dark-deeper to-dark-base rounded-3xl p-12 md:p-16 border border-white/10 overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          {/* Background elements */}
          <div className="absolute top-0 right-0 hidden md:block w-1/2 h-full bg-gradient-to-l from-neon-orange/10 to-transparent" />
          <div className="hidden md:block absolute -bottom-20 -left-20 w-60 h-60 bg-neon-orange/20 rounded-full blur-[80px]" />

          <div className="relative z-10 text-center">
            <motion.h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
            >
              Ready to Transform Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-yellow-500">
                Audio Workflow?
              </span>
            </motion.h2>

            <motion.p
              className="text-gray-400 text-sm sm:text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
            >
              Join thousands of companies using VeritoneTech Dashboard to process, 
              enhance, and manage their audio content at scale.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
            >
              <motion.button
                className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-neon-orange to-orange-500 text-white font-bold rounded-xl text-base sm:text-lg inline-flex items-center justify-center gap-2 sm:gap-3 shadow-[0_0_40px_rgba(253,128,46,0.4)]"
                whileHover={{ scale: 1.05, boxShadow: '0 0 60px rgba(253,128,46,0.6)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => { window.location.href = '/pricing'; }}
              >
                Start Your Free Trial
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </motion.button>
            </motion.div>

           
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer removed per request

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const ProductPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-dark-base min-h-screen text-white overflow-x-hidden">
      {/* Back to Home Button */}
      <div className="fixed top-4 left-4 z-50">
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-orange/10 border border-neon-orange/30 text-neon-orange hover:bg-neon-orange/20 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Back to home"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm font-medium">Home</span>
        </motion.button>
      </div>

      <div className="pb-20 md:pb-0">
        <HeroSection />
        <FeaturesSection />
        <BenefitsSection />
        <TechnologySection />
        <CTASection />
      </div>
    </div>
  );
};

export default ProductPage;
